/**
 * Панель инструментов: измерения, сравнение, экспорт, офлайн-режим.
 */

import type { MeasureMode } from "~/map/measure";
import { el } from "./dom";

export interface ToolsPanelCallbacks {
  onMeasureMode: (mode: MeasureMode) => void;
  onMeasureClear: () => void;
  onCompareToggle: (enabled: boolean) => void;
  onCompareSideChange: (side: "left" | "right", groupKey: string) => void;
  onExport: () => void;
  onOfflineDownload: () => void;
  onOfflineRemove: () => void;
}

export interface ToolsPanelOptions extends ToolsPanelCallbacks {
  groups: { key: string; title: string }[];
  compareEnabled: boolean;
  compareLeft: string | null;
  compareRight: string | null;
  offlineSupported: boolean;
  offlineStatus: string;
}

export interface ToolsPanelHandles {
  root: HTMLElement;
  measureReadout: HTMLElement;
  offlineStatus: HTMLElement;
  offlineProgress: HTMLProgressElement;
  downloadButton: HTMLButtonElement;
  removeButton: HTMLButtonElement;
}

export function createToolsPanel(options: ToolsPanelOptions): ToolsPanelHandles {
  const measureReadout = el("p", {
    class: "tools__readout",
    attrs: { role: "status", "aria-live": "polite" },
  });

  const measureButtons = (["off", "distance", "area"] as MeasureMode[]).map((mode) =>
    el("label", { class: "tools__chip" },
      el("input", {
        attrs: {
          type: "radio",
          name: "measure-mode",
          value: mode,
          ...(mode === "off" ? { checked: true } : {}),
        },
        on: {
          change: (event) => {
            if ((event.target as HTMLInputElement).checked) options.onMeasureMode(mode);
          },
        },
      }),
      el("span", {
        text: mode === "off" ? "Выключено" : mode === "distance" ? "Расстояние" : "Площадь",
      }),
    ),
  );

  const measureSection = el(
    "section",
    { class: "tools__section" },
    el("h3", { class: "tools__title", text: "Измерения" }),
    el("div", { class: "tools__chips" }, ...measureButtons),
    measureReadout,
    el("button", {
      class: "button button--ghost",
      text: "Очистить",
      attrs: { type: "button" },
      on: { click: options.onMeasureClear },
    }),
  );

  const compareSection = buildCompareSection(options);

  const exportSection = el(
    "section",
    { class: "tools__section" },
    el("h3", { class: "tools__title", text: "Экспорт" }),
    el("p", {
      class: "tools__hint",
      text: "PNG текущего вида с легендой и строкой атрибуции.",
    }),
    el("button", {
      class: "button",
      text: "Сохранить вид в PNG",
      attrs: { type: "button" },
      on: { click: options.onExport },
    }),
  );

  const offlineStatus = el("p", {
    class: "tools__readout",
    text: options.offlineStatus,
    attrs: { role: "status", "aria-live": "polite" },
  });

  const offlineProgress = el("progress", {
    class: "tools__progress",
    attrs: { max: "100", value: "0", hidden: true },
  });

  const downloadButton = el("button", {
    class: "button",
    text: "Скачать карту для офлайн-работы",
    attrs: { type: "button", ...(options.offlineSupported ? {} : { disabled: true }) },
    on: { click: options.onOfflineDownload },
  });

  const removeButton = el("button", {
    class: "button button--ghost",
    text: "Удалить офлайн-копию",
    attrs: { type: "button", ...(options.offlineSupported ? {} : { disabled: true }) },
    on: { click: options.onOfflineRemove },
  });

  const offlineSection = el(
    "section",
    { class: "tools__section" },
    el("h3", { class: "tools__title", text: "Офлайн-режим" }),
    el("p", {
      class: "tools__hint",
      text: options.offlineSupported
        ? "Архив тайлов сохраняется в хранилище браузера. После этого карта открывается без сети."
        : "Этот браузер не поддерживает локальное хранение архивов. Офлайн-режим недоступен.",
    }),
    offlineProgress,
    offlineStatus,
    el("div", { class: "tools__buttons" }, downloadButton, removeButton),
  );

  const root = el(
    "div",
    { class: "tools" },
    measureSection,
    compareSection,
    exportSection,
    offlineSection,
  );

  return { root, measureReadout, offlineStatus, offlineProgress, downloadButton, removeButton };
}

function buildCompareSection(options: ToolsPanelOptions): HTMLElement {
  if (options.groups.length < 2) {
    return el(
      "section",
      { class: "tools__section" },
      el("h3", { class: "tools__title", text: "Сравнение" }),
      el("p", {
        class: "tools__hint",
        text: "Нужно минимум два слоя генплана: например, действующая редакция и проект изменений.",
      }),
    );
  }

  const select = (side: "left" | "right", selected: string | null): HTMLElement =>
    el(
      "label",
      { class: "tools__field" },
      el("span", { text: side === "left" ? "Слева" : "Справа" }),
      el(
        "select",
        {
          class: "tools__select",
          attrs: { "aria-label": side === "left" ? "Слой слева от шторки" : "Слой справа от шторки" },
          on: {
            change: (event) => {
              options.onCompareSideChange(side, (event.target as HTMLSelectElement).value);
            },
          },
        },
        ...options.groups.map((group) =>
          el("option", {
            text: group.title,
            attrs: { value: group.key, ...(group.key === selected ? { selected: true } : {}) },
          }),
        ),
      ),
    );

  return el(
    "section",
    { class: "tools__section" },
    el("h3", { class: "tools__title", text: "Сравнение" }),
    el(
      "label",
      { class: "tools__chip" },
      el("input", {
        attrs: { type: "checkbox", ...(options.compareEnabled ? { checked: true } : {}) },
        on: {
          change: (event) => {
            options.onCompareToggle((event.target as HTMLInputElement).checked);
          },
        },
      }),
      el("span", { text: "Шторка сравнения" }),
    ),
    select("left", options.compareLeft),
    select("right", options.compareRight),
  );
}
